/**
 * Fecha y hora.
 *
 * Se muestra y se edita en la zona horaria del navegador, y se guarda en ISO con
 * zona (lo que espera un `DateTimeField` de Django con `USE_TZ`). La conversion
 * esta centralizada en `format.ts` para que no haya dos criterios distintos.
 *
 * Se parte en dos controles —la fecha en `DatePickerInput` (dd/mm/aaaa, como en
 * todo el resto de la app) y la hora en un `<input type="time">`— en vez del
 * `datetime-local` nativo, que dibujaba la parte de fecha segun el locale del SO
 * (mm/dd/aaaa en ingles). La hora si usa el control nativo: `HH:MM` no es donde
 * se cuela el formato «importado».
 */

import { controlClass } from '@/components/fields/controls';
import { DatePickerInput } from '@/components/fields/DatePickerInput';
import { ReadonlyOutput } from '@/components/fields/FieldDisplay';
import { fromDateTimeLocal, toDateTimeLocal } from '@/components/fields/format';
import type { FieldInputProps } from '@/components/fields/types';

export function DateTimeField({
  id,
  meta,
  value,
  onChange,
  readonly,
  required,
  invalid,
  describedBy,
}: FieldInputProps) {
  if (readonly) {
    return <ReadonlyOutput id={id} meta={meta} value={value} {...(describedBy === undefined ? {} : { describedBy })} />;
  }

  // El valor ISO con zona se lleva a hora local `AAAA-MM-DDTHH:mm` y se parte:
  // la fecha va al picker propio y la hora al control nativo. Al recombinar se
  // vuelve a ISO con zona por el mismo camino que ya usaba el datetime-local.
  const local = toDateTimeLocal(value);
  const fecha = local.slice(0, 10);
  const hora = local.length >= 16 ? local.slice(11, 16) : '';

  function emitir(nuevaFecha: string, nuevaHora: string): void {
    if (nuevaFecha === '') {
      // Sin fecha no hay instante posible; la hora sola no se guarda.
      onChange(null);
      return;
    }
    const horaFinal = nuevaHora === '' ? '00:00' : nuevaHora;
    onChange(fromDateTimeLocal(`${nuevaFecha}T${horaFinal}`));
  }

  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <DatePickerInput
          id={id}
          value={fecha}
          onChange={(iso) => emitir(iso ?? '', hora)}
          invalid={invalid}
          required={required}
          calendarLabel="Abrir calendario"
          {...(describedBy === undefined ? {} : { describedBy })}
        />
      </div>
      <input
        type="time"
        aria-label="Hora"
        className={controlClass(invalid, 'w-32 shrink-0 tabular-nums')}
        value={hora}
        aria-invalid={invalid || undefined}
        onChange={(evento) => {
          emitir(fecha, evento.target.value);
        }}
      />
    </div>
  );
}
